import { useToasts } from "@certn/cascadia";
import {
  Text,
  TextareaField,
  Grid,
  GridItem,
  FileUploadField,
  SelectInputField,
} from "@certn/cascadia-react";
import { Field, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { Item } from "react-stately";
import { object, string } from "yup";

import { ActionListItemWithModal } from "./ActionListItemWithModal";
import { FormModal } from "./FormModal";
import { HelpPrivacyConsent } from "./HelpPrivacyConsent";
import { useFormSubmit } from "../../../hooks/api/useSubmit";
import { baseFileTypes } from "../../../utils/file-types";

export function ReportAProblem() {
  const { t } = useTranslation();
  const { show } = useToasts();

  const submitForm = useFormSubmit("/internal/people/report-problem/", {
    onSuccess: () =>
      show({
        title: t("Submission sent"),
        type: "info",
      }),
    raiseError: true,
  });

  const menuItems = [
    {
      name: t("Google Chrome"),
      value: "GOOGLE_CHROME",
    },
    {
      name: t("Microsoft Edge"),
      value: "MICROSOFT_EDGE",
    },
    {
      name: t("Mozilla Firefox"),
      value: "MOZILLA_FIREFOX",
    },
    {
      name: t("Other"),
      value: "OTHER",
    },
  ];

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-report-a-problem-report-button"
      title={t("Report a problem")}
      description={t(
        "Let us know if you find something that doesn't work as expected"
      )}
      link={t("VERB Report", "Report")}
    >
      {(close: () => void) => (
        <Formik
          onSubmit={async (values, helpers) => {
            try {
              // so we need to cut the images variable down to a list of images
              const images = values.images.map(
                (fileInfo: { content: string }) => fileInfo.content
              );
              await submitForm(
                { ...values, images },
                helpers as FormikHelpers<{
                  images: (string | undefined)[];
                  problem_description: string;
                  browser_type: string;
                }>
              );
              close();
            } catch (e) {
              console.error("Error reporting a problem:", e);
              show({
                title: t("Something went wrong, try again"),
                type: "error",
              });
            }
          }}
          initialValues={{
            problem_description: "",
            browser_type: "",
            images: [],
          }}
          validationSchema={object().shape({
            problem_description: string().required(
              t("Please describe the problem.")
            ),
          })}
        >
          {({ handleSubmit }) => {
            return (
              <FormModal
                title={t("Report a problem")}
                onSubmit={handleSubmit}
                onCancel={close}
                continueText={t("Submit")}
                variant="success"
              >
                <Grid form>
                  <GridItem>
                    <Text style="global/body-primary">
                      {t(
                        "Let us know if you find something that doesn't work as expected."
                      )}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Field
                      name="browser_type"
                      as={SelectInputField}
                      itemKey="chrome"
                      label={t("Browser type")}
                      placeholder={t("Please select")}
                      testID="report-a-problem-modal-browser-type-select"
                    >
                      {menuItems.map((item) => (
                        <Item key={item.value}>{item.name}</Item>
                      ))}
                    </Field>
                  </GridItem>
                  <GridItem>
                    <Field
                      isRequired
                      name="problem_description"
                      as={TextareaField}
                      aria-label="Input"
                      label={t("Describe the problem")}
                      description={t(
                        "Providing details (e.g. description, device type etc.) helps us find the problem."
                      )}
                      testID="report-a-problem-modal-describe-problem-input"
                    />
                  </GridItem>
                  <GridItem>
                    <Field
                      name="images"
                      as={FileUploadField}
                      color="border-strong"
                      size="desktop"
                      label={t("Upload image or screenshot")}
                      fileTypes={baseFileTypes}
                      maxSizeBytes={10000000}
                      multiple
                      testID="report-a-problem-modal-document-upload-button"
                    />
                  </GridItem>
                  <GridItem>
                    <HelpPrivacyConsent />
                  </GridItem>
                </Grid>
              </FormModal>
            );
          }}
        </Formik>
      )}
    </ActionListItemWithModal>
  );
}
